/**
 * 부서별 월간보고서 — 작성·수정 창 (v2.280.0 · 2026-10-19 · 사용자 「작성자가 작성하기 편하게」)
 *
 * 양식(엑셀) 그대로: 1. 월간 업무 개요(소속·직급·성명·작성일 — 자동) · 2. 전월(대상 월) 업무 · 3. 당월(다음 달) 업무 계획.
 * 두 표는 줄마다 「구분 · 【키워드】 · 내용 · 비고」 — 줄 추가·지우기·순서 바꾸기, 구분·키워드는 지난 보고서·기본 구분을 자동완성.
 *
 * ⭐[📥 지난 보고서 가져오기] — 지난달 「당월(계획)」 줄이 이번 달 「전월(실적)」의 출발점이다(계획했던 것을 했다고 적는다).
 *   「고정」 업무 줄(키워드에 「고정」)은 이번 「당월」에도 그대로 넣는다 — 매달 되풀이되는 줄을 다시 안 치게.
 * ⭐처음 쓰는 부서(지난 보고서 없음)는 양식 9월분의 「구분」을 빈 줄로 깔아 준다.
 *
 * v2.281.0 — 작성자 편의 (사용자 「작성자 관점에서 작성하기 편하게」 · 2026-10-19):
 *   · [📄 미리보기] — 저장 없이 확정본과 같은 PDF 를 보고, 그 창에서 바로 [결재 상신]까지(지출결재 [확인]과 같은 흐름).
 *   · Enter 로 다음 칸 — 구분→키워드→내용(Ctrl+Enter)→비고, 비고에서 Enter 는 아래에 새 줄(같은 구분). 한글 조합 중 Enter 는 넘기지 않는다.
 *   · 내용 칸이 글 길이에 맞춰 자란다(스크롤 막대 없음) · 600자 가까우면 글자 수 표시.
 *   · ⤵ 「3. 당월」에 복사 — 전월에 한 일을 다음 달에도 이어서 할 때(같은 구분 묶음 끝에 끼운다).
 *   · 📋 엑셀에서 붙여넣기 — 양식(B~K 열 · 【 】 칸 포함)을 그대로 복사해 붙여도, 「구분·키워드·내용·비고」 네 칸이어도 줄로 바꾼다.
 *     9월분처럼 엑셀로 이미 쓴 달을 포털에 옮길 때, 또 엑셀로 초안을 잡는 사람을 위해.
 *   · 적다 만 글 지킴 — 바뀔 때마다 이 브라우저(localStorage)에 두고, 다시 열면 「불러오기」를 묻는다.
 *     (배포로 서버가 재시작되면 로그인이 풀려 저장이 실패한다 — 그때도 글은 남는다.)
 *   · 머리(대상 월)와 단추 줄(저장·상신)이 스크롤해도 붙어 있다(줄이 40개여도 단추를 찾아 내려가지 않게).
 *
 * 입력 창이라 배경 클릭·ESC 로 닫히지 않는다(useDirtyClose) — 적다 만 글은 [취소] 때 한 번 묻는다.
 * 서버가 다시 다듬는다: 앞뒤 공백 제거 · 키워드·내용·비고가 전부 빈 줄은 버림 · 상신은 「전월」에 내용 줄 1개 이상.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type TextareaHTMLAttributes } from 'react';
import {
  DEPT_REPORT_LIMITS,
  createDeptReport,
  deptPeriodText,
  nextPeriodYm,
  periodOptions,
  previewDeptReportPdf,
  updateDeptReport,
  type DeptReportRow,
} from '../lib/dept-report-api';
import { useDirtyClose } from '../lib/useDirtyClose';
import { closePdfPreview, openPdfPreview } from '../lib/pdf-preview';

type EditRow = DeptReportRow & { id: number };
type Col = 'category' | 'keyword' | 'content' | 'note';

let rowSeq = 0;
const withId = (r: DeptReportRow): EditRow => ({ ...r, id: ++rowSeq });
const emptyRow = (category = ''): EditRow => withId({ category, keyword: '', content: '', note: '' });
const strip = (rows: EditRow[]): DeptReportRow[] =>
  rows.map(({ category, keyword, content, note }) => ({ category, keyword, content, note }));
const isBlank = (r: DeptReportRow): boolean => !r.keyword.trim() && !r.content.trim() && !r.note.trim();

interface Props {
  mode: 'create' | 'edit';
  /** edit 때만 */
  docId?: number;
  /** 보고 부서(지금 이름) — 제목에 쓴다 */
  dept: string;
  initialPeriodYm: string;
  initialPrevRows: DeptReportRow[];
  initialCurRows: DeptReportRow[];
  author: { dept: string; rank: string; name: string };
  /** create 때 — 대상 월 고르기 범위의 첫 달 */
  startYm?: string;
  /** create 때 — 처음 쓸 때 깔아 줄 구분 */
  defaultCategories?: string[];
  /** create 때 — [지난 보고서 가져오기]의 원본 */
  latest?: { docNo: string; periodYm: string; curRows: DeptReportRow[] } | null;
  onClose: () => void;
  /** 저장·상신 성공 — submitted=true 면 상신까지 됐다 */
  onSaved: (submitted: boolean, docId: number) => void;
}

/* ── 📋 엑셀 붙여넣기 — 탭 구분 글을 줄로 ───────────────────────── */

/**
 * 탭 구분 글(엑셀 복사)을 칸으로 — 엑셀은 줄바꿈·따옴표가 든 칸을 쌍따옴표로 싸서 복사한다(안의 따옴표는 두 번).
 * 양식의 「자금↵회계↵세무↵경영지원」 같은 여러 줄 구분이 그렇게 온다.
 */
function splitTsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  const src = text.replace(/\r\n?/g, '\n');
  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i]!;
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') { cell += '"'; i += 1; } else { quoted = false; }
      } else {
        cell += ch;
      }
      continue;
    }
    if (ch === '"' && cell === '') { quoted = true; continue; }
    if (ch === '\t') { row.push(cell); cell = ''; continue; }
    if (ch === '\n') { row.push(cell); rows.push(row); row = []; cell = ''; continue; }
    cell += ch;
  }
  if (cell !== '' || row.length > 0) { row.push(cell); rows.push(row); }
  return rows;
}

const clean = (s: string): string => s.replace(/ /g, ' ').trim();
/** 여러 줄 구분(양식의 「자금↵회계↵세무」)은 한 줄 칸에 못 들어간다 → 「자금·회계·세무」 */
const oneLineCategory = (s: string): string => clean(s).replace(/\s*\n+\s*/g, '·');

/**
 * 붙여 넣은 글 → 줄. 두 모양을 받는다:
 *   (가) 회사 양식 그대로(B~K 열): 구분 · 【 · 키워드 · 】 · 내용 · 비고 — 「【」「】」 칸으로 알아본다
 *   (나) 네 칸: 구분 · 키워드 · 내용 · 비고 (세 칸이면 첫 칸이 구분 같을 때 구분·키워드·내용, 아니면 키워드·내용·비고 · 두 칸=키워드·내용 · 한 칸=내용)
 * 구분이 빈 줄은 바로 위 줄의 구분을 잇는다(엑셀 세로 병합이 복사되면 첫 줄에만 글이 있다). 머리글 줄(구분/내용/비고)과 빈 줄은 버린다.
 */
export function rowsFromPaste(text: string, knownCategories: readonly string[] = []): DeptReportRow[] {
  const out: DeptReportRow[] = [];
  let lastCategory = '';
  const known = new Set(knownCategories.map((c) => c.replace(/\s+/g, '')));
  for (const raw of splitTsv(text)) {
    const cells = raw.map(clean);
    // 뒤쪽 빈 칸(병합 칸 자리)은 셈에서 뺀다
    while (cells.length > 0 && cells[cells.length - 1] === '') cells.pop();
    if (cells.length === 0) continue;
    let category = '';
    let keyword = '';
    let content = '';
    let note = '';
    const open = cells.indexOf('【');
    const close = cells.indexOf('】');
    if (open >= 0 && close > open) {
      category = cells.slice(0, open).find((c) => c) ?? '';
      keyword = cells.slice(open + 1, close).filter((c) => c).join(' ');
      const rest = cells.slice(close + 1).filter((c) => c);
      content = rest[0] ?? '';
      note = rest.slice(1).join(' ');
    } else if (cells.length >= 4) {
      [category, keyword, content] = [cells[0]!, cells[1]!, cells[2]!];
      note = cells.slice(3).filter((c) => c).join(' ');
    } else if (cells.length === 3) {
      const firstLooksCategory = cells[0] === '' || known.has(cells[0]!.replace(/\s+/g, ''));
      if (firstLooksCategory) [category, keyword, content] = [cells[0]!, cells[1]!, cells[2]!];
      else [keyword, content, note] = [cells[0]!, cells[1]!, cells[2]!];
    } else if (cells.length === 2) {
      [keyword, content] = [cells[0]!, cells[1]!];
    } else {
      content = cells[0]!;
      // 표 바깥의 제목 줄(「2. 전월」 「경영지원부 월간보고 - 2026년 9월」)이 딸려 와도 줄로 만들지 않는다
      if (/^(\d\.\s*)?(전월|당월|월간 업무 개요)$/.test(content) || /월간보고\s*-/.test(content)) continue;
    }
    category = oneLineCategory(category);
    // 머리글 줄 — 「구   분」「내    용」「비   고」
    if (category.replace(/\s+/g, '') === '구분' || (!category && keyword.replace(/\s+/g, '') === '구분')) continue;
    if (!category) category = lastCategory;
    else lastCategory = category;
    const row: DeptReportRow = {
      category: category.slice(0, DEPT_REPORT_LIMITS.category),
      keyword: keyword.slice(0, DEPT_REPORT_LIMITS.keyword),
      content: content.slice(0, DEPT_REPORT_LIMITS.content),
      note: note.slice(0, DEPT_REPORT_LIMITS.note),
    };
    if (isBlank(row)) continue;
    out.push(row);
  }
  return out;
}

/* ── 적다 만 글 지킴 (localStorage) ─────────────────────────────── */

interface DraftBackup { periodYm: string; prevRows: DeptReportRow[]; curRows: DeptReportRow[]; savedAt: string }

function readBackup(key: string): DraftBackup | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const b = JSON.parse(raw) as DraftBackup;
    if (!b || !Array.isArray(b.prevRows) || !Array.isArray(b.curRows)) return null;
    return b;
  } catch {
    return null;
  }
}
function writeBackup(key: string, b: DraftBackup): void {
  try { window.localStorage.setItem(key, JSON.stringify(b)); } catch { /* 저장 공간 없음·사생활 모드 — 지킴만 못 할 뿐 */ }
}
function clearBackup(key: string): void {
  try { window.localStorage.removeItem(key); } catch { /* 무시 */ }
}
const timeText = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/* ── 내용 칸 — 글 길이에 맞춰 자라는 textarea ────────────────────── */

function GrowTextarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;   // +2 = 위아래 테두리(border-box)
  }, [props.value]);
  return <textarea ref={ref} {...props} />;
}

/* ── 한 절의 줄 편집 표 ─────────────────────────────────────────── */

function RowsEditor({ rows, onChange, listId, keywordListId, disabled, knownCategories, onCopyRow, onFlash }: {
  rows: EditRow[];
  onChange: (rows: EditRow[]) => void;
  listId: string;
  keywordListId: string;
  disabled: boolean;
  knownCategories: readonly string[];
  /** 있으면 줄마다 ⤵ (전월 → 당월 복사) */
  onCopyRow?: (r: EditRow) => void;
  onFlash: (msg: string) => void;
}) {
  const tableRef = useRef<HTMLTableElement>(null);
  // 새 줄은 아직 화면에 없다 — 그려진 뒤에 초점을 준다
  const pendingFocus = useRef<{ id: number; col: Col } | null>(null);
  useEffect(() => {
    const want = pendingFocus.current;
    if (!want) return;
    const el = tableRef.current?.querySelector<HTMLElement>(`[data-row="${want.id}"][data-col="${want.col}"]`);
    if (el) { el.focus(); pendingFocus.current = null; }
  });
  const focusCell = (id: number, col: Col) => {
    const el = tableRef.current?.querySelector<HTMLElement>(`[data-row="${id}"][data-col="${col}"]`);
    if (el) el.focus();
    else pendingFocus.current = { id, col };
  };

  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const parsed = useMemo(() => (pasteOpen ? rowsFromPaste(pasteText, knownCategories) : []), [pasteOpen, pasteText, knownCategories]);

  const set = (id: number, patch: Partial<DeptReportRow>) =>
    onChange(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const remove = (id: number) => onChange(rows.filter((r) => r.id !== id));
  const move = (id: number, dir: -1 | 1) => {
    const i = rows.findIndex((r) => r.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= rows.length) return;
    const next = rows.slice();
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  };
  /** 바로 아래에 새 줄 — 같은 구분으로 시작한다(양식에서 구분 묶음이 몇 줄씩 이어지기 때문). 만든 줄을 돌려준다 */
  const addAfter = (id: number | null): EditRow => {
    const i = id === null ? rows.length - 1 : rows.findIndex((r) => r.id === id);
    const base = rows[i];
    const next = rows.slice();
    const made = emptyRow(base?.category ?? '');
    next.splice(i + 1, 0, made);
    onChange(next);
    return made;
  };
  const applyPaste = () => {
    if (parsed.length === 0) return;
    // 깔아 둔 빈 줄(구분만 있는 자리)은 치운다 — 붙여 넣은 줄 뒤에 빈 줄이 남지 않게
    const kept = rows.filter((r) => !isBlank(r));
    onChange([...kept, ...parsed.map(withId)]);
    onFlash(`${parsed.length}줄을 붙여 넣었습니다${kept.length < rows.length ? ` (빈 줄 ${rows.length - kept.length}개는 치웠습니다)` : ''}`);
    setPasteText('');
    setPasteOpen(false);
  };

  /** Enter 로 다음 칸 — 내용은 줄바꿈이 Enter 라 Ctrl+Enter · 비고에서 Enter 는 아래 새 줄 */
  const onKey = (e: KeyboardEvent<HTMLElement>, r: EditRow, col: Col) => {
    if (e.key !== 'Enter' || disabled) return;
    if (e.nativeEvent.isComposing) return;   // 한글 조합 확정용 Enter — 칸을 넘기면 마지막 글자가 다음 칸으로 샌다
    if (col === 'content' && !(e.ctrlKey || e.metaKey)) return;
    e.preventDefault();
    if (col === 'category') focusCell(r.id, 'keyword');
    else if (col === 'keyword') focusCell(r.id, 'content');
    else if (col === 'content') focusCell(r.id, 'note');
    else {
      const made = addAfter(r.id);
      pendingFocus.current = { id: made.id, col: 'keyword' };
    }
  };

  return (
    <div className="border border-slate-200 rounded overflow-hidden">
      <table ref={tableRef} className="w-full text-[13px] table-fixed">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="px-2 py-1.5 font-medium w-[112px] text-left">구분</th>
            <th className="px-2 py-1.5 font-medium w-[150px] text-left">【 키워드 】</th>
            <th className="px-2 py-1.5 font-medium text-left">내용</th>
            <th className="px-2 py-1.5 font-medium w-[132px] text-left">비고</th>
            <th className={`px-1 py-1.5 ${onCopyRow ? 'w-[132px]' : 'w-[108px]'}`}></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-400">줄이 없습니다 — [+ 줄 추가] 또는 [📋 엑셀에서 붙여넣기]</td></tr>
          )}
          {rows.map((r, i) => (
            <tr key={r.id} className="border-t border-slate-100 align-top">
              <td className="px-1.5 py-1">
                <input value={r.category} list={listId} disabled={disabled}
                  data-row={r.id} data-col="category"
                  maxLength={DEPT_REPORT_LIMITS.category}
                  onChange={(e) => set(r.id, { category: e.target.value })}
                  onKeyDown={(e) => onKey(e, r, 'category')}
                  placeholder="인사 · 총무…" className="ui-input ui-input-sm w-full" />
              </td>
              <td className="px-1.5 py-1">
                <input value={r.keyword} list={keywordListId} disabled={disabled}
                  data-row={r.id} data-col="keyword"
                  maxLength={DEPT_REPORT_LIMITS.keyword}
                  onChange={(e) => set(r.id, { keyword: e.target.value })}
                  onKeyDown={(e) => onKey(e, r, 'keyword')}
                  placeholder="급여 · 결산…" className="ui-input ui-input-sm w-full" />
              </td>
              <td className="px-1.5 py-1">
                <GrowTextarea value={r.content} disabled={disabled} rows={1}
                  data-row={r.id} data-col="content"
                  maxLength={DEPT_REPORT_LIMITS.content}
                  onChange={(e) => set(r.id, { content: e.target.value })}
                  onKeyDown={(e) => onKey(e, r, 'content')}
                  placeholder="한 일 · 할 일을 적습니다 (Ctrl+Enter = 다음 칸)"
                  className="ui-input ui-textarea min-h-0 w-full resize-none overflow-hidden" />
                {r.content.length >= DEPT_REPORT_LIMITS.content - 100 && (
                  <div className={`text-[11px] text-right ${r.content.length >= DEPT_REPORT_LIMITS.content ? 'text-rose-600' : 'text-amber-600'}`}>
                    {r.content.length}/{DEPT_REPORT_LIMITS.content}자
                  </div>
                )}
              </td>
              <td className="px-1.5 py-1">
                <input value={r.note} disabled={disabled}
                  data-row={r.id} data-col="note"
                  maxLength={DEPT_REPORT_LIMITS.note}
                  onChange={(e) => set(r.id, { note: e.target.value })}
                  onKeyDown={(e) => onKey(e, r, 'note')}
                  placeholder="Enter = 아래 새 줄"
                  className="ui-input ui-input-sm w-full" />
              </td>
              <td className="px-1 py-1 whitespace-nowrap">
                <button type="button" disabled={disabled || i === 0} onClick={() => move(r.id, -1)}
                  className="btn btn-sm btn-ghost btn-icon" title="위로">↑</button>
                <button type="button" disabled={disabled || i === rows.length - 1} onClick={() => move(r.id, 1)}
                  className="btn btn-sm btn-ghost btn-icon" title="아래로">↓</button>
                <button type="button" disabled={disabled} onClick={() => { const made = addAfter(r.id); pendingFocus.current = { id: made.id, col: 'keyword' }; }}
                  className="btn btn-sm btn-ghost btn-icon" title="아래에 줄 추가 (같은 구분)">+</button>
                {onCopyRow && (
                  <button type="button" disabled={disabled || isBlank(r)} onClick={() => onCopyRow(r)}
                    className="btn btn-sm btn-ghost btn-icon" title="이 줄을 「3. 당월」에 복사 — 다음 달에도 이어서 할 일">⤵</button>
                )}
                <button type="button" disabled={disabled} onClick={() => remove(r.id)}
                  className="btn btn-sm btn-ghost-danger btn-icon" title="이 줄 지우기">✕</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="border-t border-slate-100 px-2 py-1.5 bg-slate-50/50 flex items-center gap-2 flex-wrap">
        <button type="button" disabled={disabled} onClick={() => { const made = addAfter(null); pendingFocus.current = { id: made.id, col: made.category ? 'keyword' : 'category' }; }}
          className="btn btn-sm btn-secondary">
          + 줄 추가
        </button>
        <button type="button" disabled={disabled} onClick={() => setPasteOpen((v) => !v)}
          className={`btn btn-sm ${pasteOpen ? 'btn-primary' : 'btn-outline'}`}
          title="엑셀에서 표 영역을 복사해 붙여 넣으면 줄로 바꿉니다">
          📋 엑셀에서 붙여넣기
        </button>
        <span className="text-xs text-slate-400">{rows.length}줄</span>
      </div>
      {pasteOpen && (
        <div className="border-t border-slate-200 px-3 py-2.5 bg-amber-50/60 space-y-2">
          <p className="text-xs text-slate-700">
            엑셀에서 표 영역을 복사해(Ctrl+C) 아래에 붙여 넣으세요(Ctrl+V). <b>양식(구분 · 【 · 키워드 · 】 · 내용 · 비고 열)을 그대로</b> 복사해도 되고,
            「구분 · 키워드 · 내용 · 비고」 네 칸 순서여도 됩니다. 구분이 빈 줄은 위 줄의 구분을 잇습니다. 머리글 줄은 알아서 뺍니다.
          </p>
          <textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)} rows={5} disabled={disabled}
            placeholder={'인사\t【\t급여\t\t】\tSM/EJ 급여, 원천세 지급 및 납부\n\t【\t근로계약\t\t】\t정규직 전환'}
            className="ui-input ui-textarea w-full font-mono text-xs" />
          <div className="flex items-center gap-2">
            <button type="button" disabled={disabled || parsed.length === 0} onClick={applyPaste} className="btn btn-sm btn-primary">
              줄로 바꾸기{parsed.length > 0 ? ` (${parsed.length}줄)` : ''}
            </button>
            <button type="button" disabled={disabled} onClick={() => { setPasteOpen(false); setPasteText(''); }} className="btn btn-sm btn-secondary">닫기</button>
            {pasteText.trim() && parsed.length === 0 && <span className="text-xs text-rose-600">줄로 읽을 글이 없습니다 — 탭으로 나뉜 칸인지 확인하세요</span>}
            {parsed.length > 0 && (
              <span className="text-xs text-slate-500 truncate">
                첫 줄: {parsed[0]!.category || '(구분 없음)'} · 【{parsed[0]!.keyword || '—'}】 {parsed[0]!.content.slice(0, 30)}{parsed[0]!.content.length > 30 ? '…' : ''}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** 지난 보고서에서 가져올 줄 — 전월 ← 지난 「당월」 전부 · 당월 ← 그중 「고정」 업무 줄(없으면 전부) */
function fromLatest(src: DeptReportRow[]): { prev: EditRow[]; cur: EditRow[] } {
  const routine = src.filter((r) => /고정/.test(r.keyword) || /고정/.test(r.content));
  return { prev: src.map(withId), cur: (routine.length > 0 ? routine : src).map(withId) };
}

export function DeptMonthlyReportFormModal(p: Props) {
  const { markDirty, resetDirty, requestClose } = useDirtyClose(p.onClose);
  const [periodYm, setPeriodYm] = useState(p.initialPeriodYm);
  // 새로 쓸 때의 출발점 — 지난 보고서가 있으면 그 줄을 미리 채워 둔다(사용자 「작성하기 편하게」 · 다시 가져오기 단추도 있다) ·
  //   없으면(처음 쓰는 부서) 양식의 기본 구분을 빈 줄로 깔아 준다
  const [prevRows, setPrevRows] = useState<EditRow[]>(() => {
    if (p.initialPrevRows.length > 0) return p.initialPrevRows.map(withId);
    if (p.mode !== 'create') return [];
    return p.latest ? fromLatest(p.latest.curRows).prev : (p.defaultCategories ?? []).map((c) => emptyRow(c));
  });
  const [curRows, setCurRows] = useState<EditRow[]>(() => {
    if (p.initialCurRows.length > 0) return p.initialCurRows.map(withId);
    if (p.mode !== 'create') return [];
    return p.latest ? fromLatest(p.latest.curRows).cur : (p.defaultCategories ?? []).map((c) => emptyRow(c));
  });
  const [busy, setBusy] = useState<'save' | 'submit' | 'preview' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const flashTimer = useRef<number | null>(null);
  const showFlash = (msg: string) => {
    setFlash(msg);
    if (flashTimer.current) window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setFlash(null), 4000);
  };
  useEffect(() => () => { if (flashTimer.current) window.clearTimeout(flashTimer.current); }, []);

  /* ── 적다 만 글 지킴 ── */
  const backupKey = `deptReportDraft:${p.dept}:${p.mode === 'edit' ? p.docId ?? 0 : 'new'}`;
  // 처음 모습 — 이것과 다를 때만 지킨다(열자마자 옛 지킴을 처음 모습으로 덮어쓰지 않게)
  const initialSnap = useRef(JSON.stringify({ periodYm: p.initialPeriodYm, prevRows: strip(prevRows), curRows: strip(curRows) }));
  const [backup, setBackup] = useState<DraftBackup | null>(() => {
    const b = readBackup(backupKey);
    if (!b) return null;
    const same = JSON.stringify({ periodYm: b.periodYm, prevRows: b.prevRows, curRows: b.curRows }) === initialSnap.current;
    const empty = b.prevRows.every(isBlank) && b.curRows.every(isBlank);
    return same || empty ? null : b;
  });
  useEffect(() => {
    const snap = { periodYm, prevRows: strip(prevRows), curRows: strip(curRows) };
    if (JSON.stringify(snap) === initialSnap.current) return;
    const t = window.setTimeout(() => writeBackup(backupKey, { ...snap, savedAt: new Date().toISOString() }), 400);
    return () => window.clearTimeout(t);
  }, [backupKey, periodYm, prevRows, curRows]);
  function restoreBackup() {
    if (!backup) return;
    if (p.mode === 'create') setPeriodYm(backup.periodYm);
    setPrevRows(backup.prevRows.map(withId));
    setCurRows(backup.curRows.map(withId));
    setBackup(null);
    markDirty();
    showFlash(`${timeText(backup.savedAt)}에 적던 글을 불러왔습니다 — 전월 ${backup.prevRows.length}줄 · 당월 ${backup.curRows.length}줄`);
  }
  function dropBackup() {
    clearBackup(backupKey);
    setBackup(null);
  }

  const options = useMemo(() => periodOptions(p.startYm ?? p.initialPeriodYm), [p.startYm, p.initialPeriodYm]);
  // 구분 자동완성 — 기본 구분 + 지난 보고서 + 지금 적힌 것
  const categories = useMemo(() => {
    const s = new Set<string>(p.defaultCategories ?? []);
    for (const r of p.latest?.curRows ?? []) if (r.category) s.add(r.category);
    for (const r of [...prevRows, ...curRows]) if (r.category) s.add(r.category);
    return [...s];
  }, [p.defaultCategories, p.latest, prevRows, curRows]);
  // 키워드 자동완성 — 지난 보고서 + 지금 적힌 것 (「고정월별업무」 같은 긴 키워드를 매달 다시 안 치게)
  const keywords = useMemo(() => {
    const s = new Set<string>();
    for (const r of [...(p.latest?.curRows ?? []), ...prevRows, ...curRows]) if (r.keyword.trim()) s.add(r.keyword.trim());
    return [...s];
  }, [p.latest, prevRows, curRows]);

  function importLatest() {
    if (!p.latest) return;
    const hasAny = [...prevRows, ...curRows].some((r) => !isBlank(r));
    if (hasAny && !window.confirm('지금 적힌 줄을 지난 보고서의 줄로 바꿉니다. 계속할까요?')) return;
    const next = fromLatest(p.latest.curRows);
    setPrevRows(next.prev);
    setCurRows(next.cur);
    markDirty();
  }

  /** ⤵ 전월 줄을 「3. 당월」에 — 같은 구분 묶음의 끝에 끼운다(세로 병합이 깨지지 않게) · 없으면 맨 뒤 */
  function copyToCur(r: EditRow) {
    setCurRows((rows) => {
      const copy = withId({ category: r.category, keyword: r.keyword, content: r.content, note: r.note });
      let at = -1;
      rows.forEach((x, i) => { if (x.category === r.category) at = i; });
      const next = rows.slice();
      next.splice(at < 0 ? next.length : at + 1, 0, copy);
      // 그 구분에 깔려 있던 빈 자리 줄은 치운다 — 복사한 줄이 그 자리를 대신한다
      return next.filter((x) => !(x.category === r.category && isBlank(x)));
    });
    markDirty();
    showFlash(`「3. 당월」에 복사했습니다 — 【${r.keyword || '키워드 없음'}】 ${r.content.slice(0, 24)}${r.content.length > 24 ? '…' : ''}`);
  }

  /** 상신할 수 없는 이유 (없으면 null) — 서버와 같은 규칙 */
  function submitProblem(): string | null {
    if (!strip(prevRows).some((r) => r.content.trim())) {
      return '「2. 전월」에 내용이 적힌 줄이 하나 이상 있어야 상신할 수 있습니다';
    }
    return null;
  }

  /** 저장·상신 본체 — 성공하면 true. 실패 문구는 창에 띄운다(서버 문구는 이미 사람이 읽을 한국어) */
  async function persist(submit: boolean): Promise<boolean> {
    setError(null);
    setBusy(submit ? 'submit' : 'save');
    try {
      const prev = strip(prevRows);
      const cur = strip(curRows);
      let docId: number;
      if (p.mode === 'edit' && p.docId) {
        const r = await updateDeptReport(p.docId, { prevRows: prev, curRows: cur, submit });
        docId = r.docId;
      } else {
        const [y, m] = periodYm.split('-').map(Number) as [number, number];
        const r = await createDeptReport({ year: y, month: m, prevRows: prev, curRows: cur, submit });
        docId = r.docId;
      }
      clearBackup(backupKey);
      resetDirty();
      p.onSaved(submit, docId);
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setBusy(null);
    }
  }

  async function save(submit: boolean) {
    setError(null);
    if (submit) {
      const bad = submitProblem();
      if (bad) { setError(bad); return; }
      const ok = window.confirm(
        `${deptPeriodText(periodYm)} ${p.dept} 월간보고서를 결재 상신합니다.\n` +
        '결재선: 담당(작성자) → 부서장 → 대표이사 (부서장 지정이 없는 부서는 그 칸이 「생략」)\n\n' +
        '상신할까요? (모양을 먼저 보려면 [📄 미리보기])',
      );
      if (!ok) return;
    }
    await persist(submit);
  }

  /** [📄 미리보기] — 저장 없이 확정본과 같은 PDF · 그 창에서 바로 상신할 수 있다 */
  async function preview() {
    setError(null);
    setBusy('preview');
    try {
      const [y, m] = periodYm.split('-').map(Number) as [number, number];
      const url = await previewDeptReportPdf({ year: y, month: m, prevRows: strip(prevRows), curRows: strip(curRows) });
      const submitLabel = p.mode === 'edit' ? '📤 다시 상신' : '📤 결재 상신';
      openPdfPreview({
        url,
        downloadUrl: url,
        filename: `${p.dept}_월간보고서_${periodYm}_미리보기.pdf`,
        mime: 'application/pdf',
        confirm: {
          message: '상신 전 모양입니다 — 문서번호는 상신할 때 붙고, 결재판의 「생략」 칸은 결재자 지정이 없는 자리입니다. 이대로 올리면 담당(작성자) → 부서장 → 대표이사 순으로 결재됩니다.',
          confirmLabel: submitLabel,
          cancelLabel: '돌아가서 수정',
          onConfirm: async () => {
            const bad = submitProblem();
            if (bad) { closePdfPreview(); setError(bad); return; }
            await persist(true);   // 성공이면 onSaved 가 이 창을 닫는다 · 실패 사유는 작성 창에 떠 있다
            closePdfPreview();
          },
        },
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  const disabled = busy !== null;
  const listId = 'dept-report-categories';
  const keywordListId = 'dept-report-keywords';

  return (
    // z-[75] — 보기 창(z-70) 위, PDF 미리보기 창(z-80 · App 에 한 번 마운트) 아래. 같은 80 이면 DOM 뒤쪽인 이 창이 미리보기를 가린다.
    <div className="fixed inset-0 bg-black/40 z-[75] flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-white rounded shadow-xl w-full max-w-5xl my-6" onInput={markDirty} onChange={markDirty}>
        {/* 머리 — 스크롤해도 붙어 있다 */}
        <div className="sticky top-0 z-10 bg-white rounded-t flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <div className="flex items-center gap-2.5 min-w-0 flex-wrap">
            <h2 className="ui-modal-title shrink-0">
              {p.dept} 월간보고서 {p.mode === 'edit' ? '수정' : '작성'}
            </h2>
            {p.mode === 'create' ? (
              <label className="flex items-center gap-1.5 text-sm text-slate-600">
                대상 월
                <select value={periodYm} onChange={(e) => setPeriodYm(e.target.value)} disabled={disabled} className="ui-input ui-input-sm">
                  {options.map((ym) => <option key={ym} value={ym}>{deptPeriodText(ym)}</option>)}
                </select>
              </label>
            ) : (
              <span className="text-sm text-slate-600">대상 월 <b>{deptPeriodText(periodYm)}</b></span>
            )}
            {flash && <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">{flash}</span>}
          </div>
          <button type="button" onClick={requestClose} className="ui-close">✕</button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* 적다 만 글 — 이 브라우저에 남아 있던 것 */}
          {backup && (
            <div className="flex items-center justify-between gap-3 flex-wrap text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded px-3 py-2">
              <span>
                ⏱ <b>{timeText(backup.savedAt)}</b>에 적다 만 글이 이 브라우저에 남아 있습니다
                (대상 월 {deptPeriodText(backup.periodYm)} · 전월 {backup.prevRows.length}줄 · 당월 {backup.curRows.length}줄).
              </span>
              <span className="flex gap-1.5 shrink-0">
                <button type="button" onClick={restoreBackup} disabled={disabled} className="btn btn-sm btn-primary">불러오기</button>
                <button type="button" onClick={dropBackup} disabled={disabled} className="btn btn-sm btn-secondary">지우기</button>
              </span>
            </div>
          )}

          {/* 1. 월간 업무 개요 — 자동 */}
          <section>
            <div className="flex items-center justify-between gap-2 flex-wrap mb-1.5">
              <div className="text-xs font-semibold text-slate-700">1. 월간 업무 개요 <span className="font-normal text-slate-400">— 자동</span></div>
              {p.mode === 'create' && p.latest && (
                <button type="button" disabled={disabled} onClick={importLatest} className="btn btn-sm btn-outline"
                  title="지난 보고서의 「당월」 줄을 이번 「전월」로 가져오고, 「고정」 업무 줄은 이번 「당월」에도 넣습니다">
                  📥 지난 보고서 가져오기 ({deptPeriodText(p.latest.periodYm)} · {p.latest.docNo})
                </button>
              )}
            </div>
            <div className="grid grid-cols-[auto_1fr_auto_1fr_auto_1fr] items-center text-sm border border-slate-200 rounded">
              <div className="bg-slate-50 px-3 py-1.5 text-slate-600 font-medium">소속</div>
              <div className="px-3 py-1.5">{p.author.dept || p.dept}</div>
              <div className="bg-slate-50 px-3 py-1.5 text-slate-600 font-medium border-l border-slate-200">직급</div>
              <div className="px-3 py-1.5">{p.author.rank || '—'}</div>
              <div className="bg-slate-50 px-3 py-1.5 text-slate-600 font-medium border-l border-slate-200">성명</div>
              <div className="px-3 py-1.5 font-semibold">{p.author.name}</div>
            </div>
            <p className="ui-help mt-1">
              작성일자는 저장한 날로 찍힙니다. 소속·직급·성명은 직원 정보에서 옵니다(틀리면 관리자에게).
              {p.mode === 'create' && p.latest && (
                <> 지난 보고서({deptPeriodText(p.latest.periodYm)})의 「당월」 줄을 아래 「전월」에 미리 채워 두었습니다 — 한 일에 맞게 고쳐 쓰세요.</>
              )}
            </p>
          </section>

          {/* 2. 전월 */}
          <section>
            <div className="text-xs font-semibold text-slate-700 mb-1.5">
              2. 전월 <span className="font-normal text-slate-500">— {deptPeriodText(periodYm)} 업무 (한 일)</span>
            </div>
            <RowsEditor rows={prevRows} onChange={setPrevRows} listId={listId} keywordListId={keywordListId} disabled={disabled}
              knownCategories={categories} onCopyRow={copyToCur} onFlash={showFlash} />
          </section>

          {/* 3. 당월 */}
          <section>
            <div className="text-xs font-semibold text-slate-700 mb-1.5">
              3. 당월 <span className="font-normal text-slate-500">— {deptPeriodText(nextPeriodYm(periodYm))} 업무 계획 (할 일)</span>
            </div>
            <RowsEditor rows={curRows} onChange={setCurRows} listId={listId} keywordListId={keywordListId} disabled={disabled}
              knownCategories={categories} onFlash={showFlash} />
          </section>

          <p className="ui-help">
            같은 「구분」이 이어지는 줄은 보고서에서 한 칸으로 세로 병합됩니다(양식과 같게). 키워드는 【 】 안에 굵게 찍힙니다.
            키워드·내용·비고가 전부 빈 줄은 저장할 때 빠집니다.
            <b className="text-slate-600"> Enter</b> 로 다음 칸(내용 칸은 Ctrl+Enter) · 비고 칸에서 Enter 는 아래에 새 줄 ·
            전월 줄의 <b className="text-slate-600">⤵</b> 는 그 일을 「3. 당월」에 복사합니다.
            적던 글은 이 브라우저에 저절로 남아, 창을 닫거나 로그인이 풀려도 다시 열면 불러올 수 있습니다.
          </p>

          {error && (
            <div className="text-sm bg-rose-50 border border-rose-200 text-rose-700 rounded px-3 py-2 whitespace-pre-line">{error}</div>
          )}
        </div>

        <datalist id={listId}>
          {categories.map((c) => <option key={c} value={c} />)}
        </datalist>
        <datalist id={keywordListId}>
          {keywords.map((k) => <option key={k} value={k} />)}
        </datalist>

        {/* 단추 — 스크롤해도 붙어 있다 */}
        <div className="sticky bottom-0 bg-white rounded-b flex items-center justify-between gap-2 border-t border-slate-200 px-5 py-3">
          <button type="button" onClick={requestClose} disabled={disabled} className="btn btn-secondary">취소</button>
          <div className="flex gap-2">
            <button type="button" onClick={() => void preview()} disabled={disabled} className="btn btn-outline"
              title="저장하지 않고, 상신했을 때와 같은 PDF 를 봅니다 — 그 창에서 바로 상신할 수도 있습니다">
              {busy === 'preview' ? '만드는 중…' : '📄 미리보기'}
            </button>
            <button type="button" onClick={() => void save(false)} disabled={disabled} className="btn btn-secondary">
              {busy === 'save' ? '저장 중…' : '💾 임시저장'}
            </button>
            <button type="button" onClick={() => void save(true)} disabled={disabled} className="btn btn-primary">
              {busy === 'submit' ? '상신 중…' : p.mode === 'edit' ? '📤 다시 상신' : '📤 결재 상신'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
